import { type ReactNode } from 'react';
import { RotateCcw } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './dropdown-menu';
import { type Table } from '@tanstack/react-table';

interface DataGridColumnVisibilityProps<TData> {
  table: Table<TData>;
  trigger: ReactNode;
  onReset?: () => void;
}

function DataGridColumnVisibility<TData>({
  table,
  trigger,
  onReset,
}: DataGridColumnVisibilityProps<TData>) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>{trigger}</DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-[180px]">
        <DropdownMenuLabel className="font-medium">Toggle Columns</DropdownMenuLabel>
        {table
          .getAllColumns()
          .filter((column) => typeof column.accessorFn !== 'undefined' && column.getCanHide())
          .map((column) => {
            return (
              <DropdownMenuCheckboxItem
                key={column.id}
                className="capitalize"
                checked={column.getIsVisible()}
                onSelect={(event) => event.preventDefault()}
                onCheckedChange={(value) => column.toggleVisibility(!!value)}
              >
                {column.columnDef.meta?.headerTitle || column.id.replace(/_/g, ' ')}
              </DropdownMenuCheckboxItem>
            );
          })}
        {onReset && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              variant="destructive"
              onSelect={() => {
                if (
                  window.confirm(
                    'Reset this grid to defaults? Your column order, sizes, visibility, sort, page size, and pinning will be cleared.',
                  )
                ) {
                  onReset();
                }
              }}
            >
              <RotateCcw className="size-3.5" />
              Reset to defaults
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export { DataGridColumnVisibility };
